import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ContratLocation } from '../models/models'; // Ensure to define ContratLocation model
import api from './api';

interface ContratLocationState {
  contratLocationData: ContratLocation[];
  total: number;
  totalPages: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  selectedContratLocation: ContratLocation | null;
}

const initialState: ContratLocationState = {
  contratLocationData: [],
  total: 0,
  totalPages: 0,
  currentPage: 0,
  loading: false,
  error: null,
  successMessage: null,
  selectedContratLocation: null,
};

// Fetch all ContratLocation with pagination
export const fetchContratLocationData = createAsyncThunk<
  { data: ContratLocation[]; total: number; totalPages: number; currentPage: number },
  { limit: number; offset: number },
  { rejectValue: string }
>(
  'contratLocation/fetchContratLocationData',
  async ({ limit, offset }, thunkAPI) => {
    try {
      const response = await api.get('/contratlocations/getAll', { params: { limit, offset } });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to load data.');
    }
  }
);

// Fetch ContratLocation by name
export const fetchContratLocationByLocataire = createAsyncThunk<
  { data: ContratLocation[]; total: number; totalPages: number; currentPage: number },
  { name: string; limit: number; offset: number },
  { rejectValue: string }
>(
  'contratLocation/fetchContratLocationByLocataire',
  async ({ name, limit, offset }, thunkAPI) => {
    try {
      const response = await api.get('/contratlocations/getLocataire', { params: { name, limit, offset } });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to load data.');
    }
  }
);

// Create or update a ContratLocation
export const createContratLocation = createAsyncThunk<
  ContratLocation,
  ContratLocation,
  { rejectValue: string }
>(
  'contratLocation/createOrUpdateContratLocation',
  async (contratLocationData, thunkAPI) => {
    try {
      const response = await api.post('/contratlocations/createOrUpdate', contratLocationData);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to create or update.');
    }
  }
);

// Delete a ContratLocation
export const deleteContratLocation = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>(
  'contratLocation/deleteContratLocation',
  async (contratLocationId, thunkAPI) => {
    try {
      await api.delete(`/contratlocations`, { params: { id:contratLocationId } });
      return contratLocationId;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to delete.');
    }
  }
);

// Fetch ContratLocation by filters (POST)
export const fetchContratLocationByFilters = createAsyncThunk<
  { data: ContratLocation[]; total: number; totalPages: number; currentPage: number },
  {
    limit: number;
    offset: number;
    numero_contrat?: string | null;
    locataire_id?: number | null;
    bien_id?: number | null;
    date_debut?: string | null;
    date_fin?: string | null;
  },
  { rejectValue: string }
>(
  'contratLocation/fetchContratLocationByFilters',
  async (filters, thunkAPI) => {
    try {
      const response = await api.post('/contratlocations/fetchContratByFilters', filters);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to filter contracts.');
    }
  }
);



const contratLocationSlice = createSlice({
  name: 'contratLocation',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setSelectedContratLocation: (state, action: PayloadAction<ContratLocation | null>) => {
      state.selectedContratLocation = action.payload;
    },
    clearContratLocationData: (state) => {
      state.contratLocationData = [];
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContratLocationData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContratLocationData.fulfilled, (state, action) => {
        state.loading = false;
        state.contratLocationData = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchContratLocationData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchContratLocationByLocataire.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContratLocationByLocataire.fulfilled, (state, action) => {
        state.loading = false;
        state.contratLocationData = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchContratLocationByLocataire.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createContratLocation.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createContratLocation.fulfilled, (state, action: PayloadAction<ContratLocation>) => {
        state.loading = false;
        state.successMessage = 'Creation/Update successful!';
      })
      .addCase(createContratLocation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteContratLocation.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(deleteContratLocation.fulfilled, (state, action: PayloadAction<number>) => {
        state.loading = false;
        state.successMessage = 'Deletion successful!';
      })
      .addCase(deleteContratLocation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchContratLocationByFilters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContratLocationByFilters.fulfilled, (state, action) => {
        state.loading = false;
        state.contratLocationData = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchContratLocationByFilters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
      
  },
});

export const { setCurrentPage, setSelectedContratLocation, clearMessages, clearContratLocationData } = contratLocationSlice.actions;
export default contratLocationSlice.reducer;
